<script setup>
import { getBusinessChannel } from '@/api/business'
import { getInventoryByProductId, getStockAllocations, saveStockAllocations } from '@/api/inventory'
import { getProductDetail } from '@/api/product'
import { getHashWarehouseList } from '@/api/warehouse'
import IconStockAllocated from '@/components/Icons/Inventory/IconStockAllocated.vue'
import IconStockOnHand from '@/components/Icons/Inventory/IconStockOnHand.vue'
import IconStockSellable from '@/components/Icons/Inventory/IconStockSellable.vue'
import { salesChannelIcon } from '@/utils/salesChannelIcon'
import {
  templateRef,
  useAsyncState,
  useResizeObserver,
  useWindowSize,
} from '@vueuse/core'
import { computed, getCurrentInstance, ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { i18n } from '@/i18n'


const { $notification } = getCurrentInstance().proxy.$root

const route = useRoute()
const router = useRouter()

const id = computed(() => route.params.product_id || route.params.id)

const { state: product, isLoading: isProductFetching } = useAsyncState(
  async () =>
    await getProductDetail({ id: id.value }).then(({ data }) => data?.data),
  null,
)

/**
 * 
 * @param {import('@/api/inventory.d').StockAllocationResponse['data']} allocationItems 
 * @param warehouses 
 * @param channels 
 * @param inventories 
 */
function parseAllocationItem(
  allocationItems,
  warehouses,
  channels,
  inventories,
) {
  /**
   * @type {import('@/types/allocation').CatalogAllocationDetailByWarehouse}
   */
  const items = []

  for (const inventory of inventories.warehouses) {
    const warehouseId = inventory.id
    if (!items?.[warehouseId]) {
      items[warehouseId] = {
        name: warehouses[warehouseId]?.name || i18n.t('productAvailability.unknown_warehouse'),
        id: warehouseId,
        unknown: false,
        counts: 0,
        items: [],
      }
    }

    const allocationCatalogs = (allocationItems.find(item => item.warehouse_id === warehouseId)?.catalogs) || [];


    (product.value.catalogs || []).forEach((catalog, index) => {
      const inventoryCatalog = inventory.catalogs.find(c => c.id === catalog.id)

      if (!inventoryCatalog) {
        return
      }

      const allocationCatalog = allocationCatalogs.find(c => c.catalog_id === inventoryCatalog.id)
      const allocations = (allocationCatalog?.allocations || [])
          .map(item => ({
            ...item,
            info: channels.find(channel => channel.id === item.channel_id),
          }))
          .toSorted((a, b) => a.channel_id - b.channel_id)

      items[warehouseId].counts += allocations.length
      items[warehouseId].items.push({
        catalog_id: catalog.id,
        title: catalog.title,
        sku: catalog.sku,
        inventory: inventoryCatalog,
        enable: allocationCatalog?.enable ?? true,
        allocations: allocations,
      })
    })
  }

  const results = Object.values(items).toSorted((a, b) => {
    return ((a.unknown ? '9999-' : '')  +!a.counts + a.name.toLowerCase()).localeCompare(
      (b.unknown ? '9999-' : '') + !b.counts + b.name.toLowerCase(),
    )
  })
  return results
}

const { state: allocations, execute: fetchAllocations, isLoading } = useAsyncState(
  async () => {
    const [allocationItems, warehouses, channels, inventories] =
      await Promise.all([
        getStockAllocations(product.value.product_id, {
          headers: {
            'Business-Id': product.value.business_id,
          },
        }).then(({ data }) => data?.data || []),
        getHashWarehouseList(product.value.business_id),
        getBusinessChannel(product.value.business_id).then(
          ({ data }) => data?.data?.channels || [],
        ),
        getInventoryByProductId({
          product_id: product.value.product_id,
          params: { business_id: product.value.business_id },
        }).then(({ data }) => data?.data),
      ])

    return parseAllocationItem(
      allocationItems,
      warehouses,
      channels,
      inventories,
    )
  },
  {},
  {
    immediate: false,
    shallow: false,
    onError: err => {
      console.error(err);
      $notification.error({
        message: i18n.t('common.error_occurred'),
        description: err.message,
      })
    },
  },
)

/**
 * 
 * @typedef {import('@/types/allocation').CatalogAllocationDetail} CatalogAllocationDetail
 * 
 * @param {CatalogAllocationDetail['channels'][0]} allocationChannel 
 * @param {CatalogAllocationDetail['inventory']} inventory 
 */
function calculateAllocationAmount(allocationChannel, inventory) {
  return Math.floor(allocationChannel.allocate_value / 100 * inventory.sellable)
}

const tabPosition = ref('left')
const wrapper = templateRef('wrapper')
useResizeObserver(wrapper, entries => {
  const entry = entries[0]
  const width = entry.contentRect.width
  tabPosition.value = width < 768 ? 'top' : 'left'
})

watch(
  () => product.value?.product_id,
  (id, old) => {
    if (id && id !== old) fetchAllocations()
  },
)

const selectedWarehouse = computed(
  () => route.query.warehouse_id || allocations.value?.[0]?.id,
)

const saving = ref(false)
async function saveAllocation() {
  saving.value = true
  try {
    await saveStockAllocations(
      product.value.business_id,
      product.value.product_id, 
      {
        warehouses: allocations.value.map(item => ({
          warehouse_id: item.id,
          catalogs: item.items.map(({ inventory, ...catalog}) => ({
            ...catalog,
          })),
        })),
      },
    )
    await fetchAllocations()
    $notification.success({
      message: i18n.t('productAvailability.allocation_saved'),
    })
  } catch (error) {
    console.error(error)
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div ref="wrapper">
    <div class="mb-5 d-flex page-title">
      <a href="#" style="color: #495057" class="py-1 mr-3" @click="$router.back()">
        <a-icon type="arrow-left" />
      </a>

      <div>
        <h1 class="h4">
          {{ $t('product.stock_allocation') }}
        </h1>
        <div class="product-title">
          {{ product?.detail?.title || '-' }}
        </div>
        <div>
          {{ $tc('product.variant', product?.catalogs?.length) }}
        </div>
      </div>
    </div>

    <div class="" style="position: relative;">
      <div v-if="isLoading" class="spinner-wrapper">
        <a-spin spinning size="large" />
      </div>


      <a-tabs
        v-else
        :tab-position="tabPosition"
        color="primary"
        size="small"
        class="allocation-tabs"
        :animated="false"
        :active-key="selectedWarehouse"
        @change="
          $router.replace({ query: { ...$route.query, warehouse_id: $event } })
        "
      >
        <template v-if="tabPosition === 'left'" #tabBarExtraContent>
          <div class="w-full text-center mb-3" style="margin-right: -1px; letter-spacing: 1px; text-transform: uppercase; color: #495057; border-bottom: 1px solid #e4e9f0; border-right: 1px solid #F8F9FA">
            {{ $t('productAvailability.warehouse') }}
          </div>
        </template>
        <a-tab-pane v-for="warehouse in allocations" :key="warehouse.id" :warehouse-id="warehouse.id">
          <template #tab>
            <span :warehouse-id="warehouse.id">
              {{ warehouse.name }}
            </span>
            <a-badge
              :show-zero="false"
              :count="warehouse.counts"
              :offset="[15, 0]"
              style="position: absolute; right: 1.25rem"
              :number-style="{
                backgroundColor: 'rgb(255, 10, 84, .5)',
                fontSize: '80%',
              }"
            />
          </template>

          <Transition name="slide-fade">
            <div v-if="warehouse.id === selectedWarehouse">
              <a-card
                v-for="item in warehouse.items"
                :id="item.catalog_id"
                :key="item.catalog_id"
                :data-catalog-id="item.catalog_id"
                class="mb-5"
              >
                <template #title>
                  <div class="catalog-info">
                    <div>
                      <div class="catalog-info__sku">
                        {{ item.sku }}
                      </div>
                      <div class="catalog-info__title">
                        {{ item.title }}
                      </div>

                      <div class="stock-detail">
                        <a-tooltip :title="$t('productAvailability.on_hand_stock')">
                          <IconStockOnHand width="12" />
                          {{ item.inventory.on_hand || 0 }}
                        </a-tooltip>
                        <a-tooltip :title="$t('productAvailability.allocated_stock')">
                          <IconStockAllocated width="12" />
                          {{ item.inventory.allocated || 0 }}
                        </a-tooltip>
                        <a-tooltip :title="$t('productAvailability.sellable_stock_short')">
                          <IconStockSellable width="12" />
                          {{ item.inventory.sellable || 0 }}
                        </a-tooltip>
                      </div>
                    </div>

                    <div class="ml-auto">
                      <div class="mb-2">
                        {{ $t('productAvailability.enable_stock_push') }}
                      </div>
                      <a-switch v-model="item.enable" />
                    </div>
                  </div>
                </template>

                <table class="channel-allocation">
                  <colgroup>
                    <col style="min-width: 100px" />
                    <col style="width: 100px" />
                    <col style="width: 100px" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{{ $t('productAvailability.channel') }}</th>
                      <th>{{ $t('productAvailability.allocation') }}</th>
                      <th class="text-right">
                        {{ $t('productAvailability.stock') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="!item.allocations.length">
                      <tr>
                        <td colspan="3">
                          <a-empty />
                        </td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr
                        v-for="(channel, index) in item.allocations"
                        :key="[channel.channel_id, index].join('--')"
                        class="channel-allocation--item"
                      >
                        <td>
                          <div class="channel-info">
                            <div>
                              <img :src="salesChannelIcon(channel.info?.code)" />
                            </div>
                            <div v-if="channel.info">
                              <div class="small text-secondary channel-info__id">
                                #{{ channel.info?.id }}
                              </div>
                              {{ channel.info?.title || '-' }}
                            </div>
                            <div
                              v-else
                              class="text-muted small"
                              style="font-style: italic"
                            >
                              {{ $t('productAvailability.deleted_channel') }}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="mb-0 mr-3">
                              <a-tooltip :title="!item.enable ? $t('productAvailability.stock_push_disabled_sku') : `${$t('productAvailability.stock_push_label')}: ${channel.enable_sync ? $t('productAvailability.stock_push_enabled') : $t('productAvailability.stock_push_disabled')}`">
                                <a-switch
                                  v-model="channel.enable"
                                  size="small"
                                  :disabled="!item.enable"
                                />
                              </a-tooltip>
                            </div>
                            <div>
                              <a-input-number
                                v-model="channel.allocate_value"
                                :min="0"
                                :max="100"
                                :formatter="value => `${value}%`"
                                :parser="value => value.replace('%', '')"
                              />
                            </div>
                          </div>
                        </td>
                        <td class="text-right">
                          {{
                            Intl.NumberFormat('id-ID').format(
                              calculateAllocationAmount(channel, item.inventory),
                            )
                          }}
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </a-card>
            </div>
          </Transition>
        </a-tab-pane>
      </a-tabs>
    </div>

    <div class="allocation-footer">
      <a-button
        type="primary"
        size="large"
        :loading="saving"
        :disabled="saving"
        @click="saveAllocation"
      >
        {{ $t('common.save') }}
      </a-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.spinner-wrapper {
  background-color: rgba(255, 255, 255, 0.8);
  height: 300px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.page-title {
  color: rgb(73, 80, 87);
  letter-spacing: 2px;
  text-transform: uppercase;
}

.product-title {
  font-size: 16px;
  font-weight: 500;
  // margin-top: -.5rem;
  letter-spacing: 0px;
  text-transform: none;
}

.allocation-footer {
  position: sticky;
  bottom: 0;
  background-color: #f8f9fa;
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  z-index: 10;
}

.allocation-tabs.ant-tabs::v-deep {
  .ant-tabs-left-bar {
    
    display: flex;
    flex-direction: column;

    .ant-tabs-extra-content {
      order: -1;
    }

    .ant-tabs-tab {
      text-align: left;
      border-radius: 4px;
      margin-bottom: 2px;
      padding: 0.5rem 2rem 0.5rem 1rem;
      min-width: 175px;
      max-width: 200px;
      text-wrap: auto;
      margin-right: 0.5rem !important;
      display: flex;
      font-size: 13px;

      > *:not(.ant-badge) {
        flex-grow: 1;
      }

      .ant-badge {
        font-size: 12px;
        line-height: 1.5;
      }

      &.ant-tabs-tab-active {
        background-color: #ffebf1;
      }
    }
  }

  .ant-tabs-top-bar {
    .ant-tabs-tab {
      padding: 8px 20px;

      .ant-badge {
        font-size: 11px;
        top: 0;
        // position: relative !important;
        right: -.75rem !important;
        // transform: none;

        sup {
          right: 0 !important;
        }
      }
    }
  }
}

.catalog-info {
  display: flex;
  gap: 0 12px;
  align-items: center;
  white-space: initial;

  > *:nth-child(1) {
    flex-shrink: 1;
  }

  > *:nth-child(2) {
    text-align: right;
    font-size: 12px;
    font-weight: normal;
    flex-shrink: 0;
  }

  &__sku {
    font-size: 12px;
    color: #444444;
    letter-spacing: 0.025rem;
    font-family: monospace;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: var(--kit-color-primary);
  }
}

.stock-detail {
  display: flex;
  gap: 0 12px;
  font-size: 14px;
  color: #868686;
  letter-spacing: 0.025rem;
  font-family: monospace;

  > * {
    display: flex;
    gap: 0 4px;
    align-items: center;
  }
}

.channel-info {
  display: flex;
  gap: 0 16px;
  align-items: center;

  &__id {
    font-size: 11px;
    color: #656565;
    letter-spacing: 0.025rem;
    font-family: monospace;
    margin-bottom: 0;
    line-height: 1.25;
  }
}

.channel-allocation {
  width: 100%;

  thead {
    font-weight: 500;
    font-size: 90%;
    border-bottom: 1px solid #efefef;
  }

  th {
    padding: 0.5rem 0.5rem;
  }

  tbody {
    td {
      padding: 1rem 0.5rem;
    }

    tr:not(:last-child) {
      border-bottom: 1px solid #efefef;
    }
  }
}
</style>
